import React, { useState, useEffect } from 'react';
import { BarChart3, Search, RefreshCw, Package, MapPin, Filter, X } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { ItemBinsDto } from '../../../../../types/dataInspection';
import { BackButton } from '../../../../common/BackButton';

interface ItemBinsViewerProps {
    onBack: () => void;
}

export const ItemBinsViewer: React.FC<ItemBinsViewerProps> = ({ onBack }) => {
    const [itemBins, setItemBins] = useState<ItemBinsDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<'all' | 'item' | 'bin'>('all');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        loadItemBins();
    }, []);

    const loadItemBins = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getAllItemBins();
            setItemBins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const searchByItem = async (itemNo: string) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getItemBinsByItemNo(itemNo);
            setItemBins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const searchByBin = async (binCode: string) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getItemBinsByBinCode(binCode);
            setItemBins(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (!searchTerm.trim()) {
            loadItemBins();
            return;
        }

        switch (filterType) {
            case 'item':
                searchByItem(searchTerm);
                break;
            case 'bin':
                searchByBin(searchTerm);
                break;
            default:
                // For 'all', just filter locally
                break;
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterType('all');
        loadItemBins();
    };

    const filteredItemBins = filterType === 'all' ? itemBins.filter(item => 
        item.itemNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.binCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.binDescription?.toLowerCase().includes(searchTerm.toLowerCase())
    ) : itemBins;

    const getQuantityColor = (quantity: number, reserved: number) => {
        const available = quantity - reserved;
        if (available <= 0) return 'text-red-600';
        if (available <= 10) return 'text-yellow-600';
        return 'text-green-600';
    };

    const totalQuantity = filteredItemBins.reduce((sum, item) => sum + item.quantity, 0);
    const totalReserved = filteredItemBins.reduce((sum, item) => sum + item.quantityReserved, 0);
    const totalAvailable = filteredItemBins.reduce((sum, item) => sum + item.quantityAvailable, 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-teal-600" />
                    <h1 className="text-xl font-semibold">Lagersaldo</h1>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Total mengde</p>
                            <p className="text-2xl font-bold text-gray-900">{totalQuantity.toLocaleString()}</p>
                        </div>
                        <Package className="w-8 h-8 text-blue-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Reservert</p>
                            <p className="text-2xl font-bold text-yellow-600">{totalReserved.toLocaleString()}</p>
                        </div>
                        <Package className="w-8 h-8 text-yellow-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Tilgjengelig</p>
                            <p className="text-2xl font-bold text-green-600">{totalAvailable.toLocaleString()}</p>
                        </div>
                        <Package className="w-8 h-8 text-green-500" />
                    </div>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Søk etter vare eller lokasjon..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <Filter className="w-4 h-4" />
                                Filter
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleSearch}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                <Search className="w-4 h-4" />
                                Søk
                            </button>
                            <button
                                onClick={loadItemBins}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                Alle
                            </button>
                        </div>
                    </div>

                    {/* Filter Options */}
                    {showFilters && (
                        <div className="border-t pt-4">
                            <div className="flex items-center gap-4">
                                <span className="text-sm font-medium text-gray-700">Søketype:</span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setFilterType('all')}
                                        className={`px-3 py-1 rounded-full text-sm ${filterType === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                                    >
                                        Alle
                                    </button>
                                    <button
                                        onClick={() => setFilterType('item')}
                                        className={`px-3 py-1 rounded-full text-sm ${filterType === 'item' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                                    >
                                        Bare vare
                                    </button>
                                    <button
                                        onClick={() => setFilterType('bin')}
                                        className={`px-3 py-1 rounded-full text-sm ${filterType === 'bin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                                    >
                                        Bare lokasjon
                                    </button>
                                </div>
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-3 h-3" />
                                    Nullstill
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* ItemBins Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Lagersaldo ({filteredItemBins.length} poster)</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vare</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lokasjon</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lager</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mengde</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reservert</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tilgjengelig</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kostpris</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oppdatert</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredItemBins.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {item.itemNo}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                        {item.itemDescription || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                            {item.binCode}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.warehouseID}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {item.quantity.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600">
                                        {item.quantityReserved.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <span className={getQuantityColor(item.quantity, item.quantityReserved)}>
                                            {item.quantityAvailable.toLocaleString()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.itemBatchNo || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.unitCost ? `${item.unitCost.toFixed(2)} kr` : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.lastUpdate ? new Date(item.lastUpdate).toLocaleDateString('nb-NO') : 'N/A'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};